import React from 'react';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { IMouseEvent } from '../../../models/IMouseEvent.js';
import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import Button from '../Button/Button.js';
import { IIconProps } from '../Icon/models/index.js';

import { IToggleButtonConfiguration, IToggleButtonProps, IToggleButtonState } from './models/index.js';
import Wrapper from './styledComponents/Wrapper.js';
import { TOGGLE_BUTTON_CATEGORY_KEY, TOGGLE_BUTTON_COMPONENT_KEY } from './ToggleButton.meta.js';

export class ToggleButton extends ComponentBase<IToggleButtonProps, IToggleButtonState, IToggleButtonConfiguration> {
	categoryKey = TOGGLE_BUTTON_CATEGORY_KEY;
	componentKey = TOGGLE_BUTTON_COMPONENT_KEY;

	constructor(props: IToggleButtonProps) {
		super(props);

		makeObservable<ToggleButton, 'active'>(this, {
			active: observable.ref,
			handleToggle: action,
			iconProps: computed,
		});

		this.active = !!props.active;
		this.disabled = !!props.disabled;
	}

	private active = false;
	private disabled = false;

	handleToggle = async (event: IMouseEvent) => {
		if (!this.disabled) {
			event.stopPropagation();
			this.active = !this.active;
			const { onChange } = this.props;
			if (onChange) {
				await onChange(this.active);
			}
		}
	};

	get iconLoadingDefault(): IIconProps {
		return {
			asset: 'base.toggleButton.loading',
			className: 'ksys-toggle-button-loading',
		};
	}

	get iconActiveDefault(): IIconProps {
		return {
			asset: 'base.toggleButton.active',
			accessibilityText: 'Active',
		};
	}

	get iconInactiveDefault(): IIconProps {
		return {
			asset: 'base.toggleButton.inactive',
			accessibilityText: 'Inactive',
		};
	}

	get iconDisabledInactiveDefault(): IIconProps {
		return {
			asset: 'base.toggleButton.disabledInactive',
			accessibilityText: 'Disabled Inactive',
		};
	}

	get iconProps(): IIconProps {
		const { loading = false, iconActive, iconInactive, iconDisabledInactive, iconLoading } = this.props;
		if (loading) {
			return iconLoading || this.iconLoadingDefault;
		}
		if (this.disabled) {
			return iconDisabledInactive || this.iconDisabledInactiveDefault;
		} else if (this.active) {
			return iconActive || this.iconActiveDefault;
		}
		return iconInactive || this.iconInactiveDefault;
	}

	render() {
		const { className, buttonProps = {}, loading = false, suppressAnalytics, analyticsContext } = this.props;
		const iconProps = this.iconProps;
		return (
			<Wrapper className={className}>
				<Button
					{...buttonProps}
					disabled={loading}
					onClick={this.handleToggle}
					iconProps={iconProps}
					aria-live="polite"
					expanded={this.active}
					suppressAnalytics={suppressAnalytics}
					analyticsContext={analyticsContext}
				/>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(ToggleButton), TOGGLE_BUTTON_CATEGORY_KEY, TOGGLE_BUTTON_COMPONENT_KEY);
